import { alpha, createTheme, ThemeOptions } from '@mui/material/styles';
import palette from './palette';
import { brandColors, backgrounds, interactions } from './colors';
import { createComponentOverrides } from './components';

// Extend the theme interface
declare module '@mui/material/styles' {
  interface Theme {
    customBackgrounds: {
      stepCard: string;
      footer: string;
      modal: string;
      card: string;
      landing: string;
    };
    customColors: {
      purple: typeof palette.purple;
    };
    actionAlpha: {
      hover: string;
      selected: string;
      focus: string;
      disabled: string;
    };
    statusBackgrounds: {
      successLight: string;
      successMedium: string;
      warningLight: string;
      warningMedium: string;
      errorLight: string;
      errorMedium: string;
      infoLight: string;
      infoMedium: string;
      primaryLight: string;
      primaryMedium: string;
    };
    statusAlpha: {
      success: string;
      warning: string;
      error: string;
      info: string;
    };
    commonBackgrounds: {
      cardBg: string;
      glassBg: string;
    };
    textColors: {
      muted: string;
    };
  }

  interface ThemeOptions {
    customBackgrounds?: {
      stepCard: string;
      footer: string;
      modal: string;
      card: string;
      landing: string;
    };
    customColors?: {
      purple: typeof palette.purple;
    };
    actionAlpha?: {
      hover: string;
      selected: string;
      focus: string;
      disabled: string;
    };
    statusBackgrounds?: {
      successLight: string;
      successMedium: string;
      warningLight: string;
      warningMedium: string;
      errorLight: string;
      errorMedium: string;
      infoLight: string;
      infoMedium: string;
      primaryLight: string;
      primaryMedium: string;
    };
    statusAlpha?: {
      success: string;
      warning: string;
      error: string;
      info: string;
    };
    commonBackgrounds?: {
      cardBg: string;
      glassBg: string;
    };
    textColors?: {
      muted: string;
    };
  }
}

/**
 * Base typography configuration (shared structure, different weights for themes)
 */
const typography = {
  // Hero/Landing page main title
  h1: {
    fontSize: '2.25rem', // 36px mobile
    fontWeight: 700,
    lineHeight: 1.2,
    '@media (min-width:600px)': {
      fontSize: '3.75rem', // 60px desktop
    },
  },
  // Page/Section titles
  h2: {
    fontSize: '1.875rem', // 30px mobile
    fontWeight: 600,
    lineHeight: 1.3,
    '@media (min-width:600px)': {
      fontSize: '2.25rem', // 36px desktop
    },
  },
  // Card/Component titles
  h3: {
    fontSize: '1.5rem', // 24px mobile
    fontWeight: 600,
    lineHeight: 1.4,
    '@media (min-width:600px)': {
      fontSize: '1.875rem', // 30px desktop
    },
  },
  // Onboarding step titles
  h4: {
    fontSize: '1.25rem', // 20px mobile
    fontWeight: 600,
    lineHeight: 1.4,
    '@media (min-width:600px)': {
      fontSize: '1.5rem', // 24px desktop
    },
  },
  // Section subtitles
  h5: {
    fontSize: '1.125rem', // 18px
    fontWeight: 500,
    lineHeight: 1.5,
  },
  // Small headings/labels
  h6: {
    fontSize: '1rem', // 16px
    fontWeight: 500,
    lineHeight: 1.5,
  },
  // Main body text (descriptions, content)
  body1: {
    fontSize: '1rem', // 16px
    fontWeight: 400,
    lineHeight: 1.6,
  },
  // Secondary/smaller body text
  body2: {
    fontSize: '0.875rem', // 14px
    fontWeight: 400,
    lineHeight: 1.5,
  },
  // Large body text (hero descriptions)
  subtitle1: {
    fontSize: '1.125rem', // 18px
    fontWeight: 400,
    lineHeight: 1.6,
    '@media (min-width:600px)': {
      fontSize: '1.25rem', // 20px desktop
    },
  },
  // Medium body text (step descriptions)
  subtitle2: {
    fontSize: '1rem', // 16px
    fontWeight: 400,
    lineHeight: 1.5,
    '@media (min-width:600px)': {
      fontSize: '1.125rem', // 18px desktop
    },
  },
  // Form labels, small UI text
  caption: {
    fontSize: '0.75rem', // 12px
    fontWeight: 400,
    lineHeight: 1.4,
  },
  // Button text
  button: {
    fontSize: '1rem', // 16px
    fontWeight: 500,
    textTransform: 'none' as const,
    lineHeight: 1.5,
  },
} as const;

/**
 * Shared theme properties that don't depend on light/dark mode
 * Includes both standard MUI properties and custom theme extensions
 */
const sharedThemeProperties: Partial<ThemeOptions> = {
  shape: {
    borderRadius: 4, // MUI default divided by 2
  },
  typography,
  customColors: {
    purple: palette.purple,
  },
  actionAlpha: interactions.primary,
  statusBackgrounds: {
    successLight: alpha('#2e7d32', 0.08),
    successMedium: alpha('#2e7d32', 0.15),
    warningLight: alpha('#ed6c02', 0.08),
    warningMedium: alpha('#ed6c02', 0.15),
    errorLight: alpha('#d32f2f', 0.08),
    errorMedium: alpha('#d32f2f', 0.15),
    infoLight: alpha('#0288d1', 0.08),
    infoMedium: alpha('#0288d1', 0.15),
    primaryLight: alpha(brandColors.primary, 0.08),
    primaryMedium: alpha(brandColors.primary, 0.15),
  },
  statusAlpha: {
    success: alpha('#2e7d32', 0.1),
    warning: alpha('#ed6c02', 0.1),
    error: alpha('#d32f2f', 0.1),
    info: alpha('#0288d1', 0.1),
  },
};

/**
 * Create theme configuration for dark mode (public routes)
 */
export const createDarkTheme = () => {
  const baseTheme: ThemeOptions = {
    ...sharedThemeProperties,
    palette: {
      mode: 'dark',
      primary: {
        main: brandColors.primary,
        light: alpha(brandColors.primary, 0.1),
        dark: brandColors.tertiary,
        contrastText: palette.white,
      },
      secondary: {
        main: '#220135',
        light: brandColors.primary,
        dark: palette.purple[800],
        contrastText: palette.white,
      },
      success: {
        main: '#2e7d32',
        light: '#4caf50',
        dark: '#1d8325',
        contrastText: '#FFFFFFcc',
      },
      warning: {
        main: '#ed6c02',
        light: '#ff9800',
        dark: '#cd5717',
        contrastText: '#FFFFFFcc',
      },
      error: {
        main: '#d32f2f',
        light: '#f44336',
        dark: '#b72f2f',
        contrastText: '#FFFFFFcc',
      },
      background: {
        default: backgrounds.dark.default,
        paper: palette.purple[900],
      },
      text: {
        primary: palette.purple[100],
        secondary: alpha(palette.purple[100], 0.8),
      },
    },
    customBackgrounds: {
      stepCard: backgrounds.dark.paper,
      footer: backgrounds.dark.footer,
      modal: backgrounds.dark.modal,
      card: backgrounds.dark.card,
      landing: backgrounds.dark.default,
    },
    commonBackgrounds: {
      cardBg: alpha(palette.purple[900], 0.3),
      glassBg: alpha(palette.purple[900], 0.2),
    },
    textColors: {
      muted: alpha(palette.purple[300], 0.7),
    },
  };

  return createTheme({
    ...baseTheme,
    components: createComponentOverrides('dark'),
    cssVariables: true, // Enable CSS variables for theming
  });
};

/**
 * Create theme configuration for light mode (private routes)
 */
export const createLightTheme = () => {
  const baseTheme: ThemeOptions = {
    ...sharedThemeProperties,
    palette: {
      mode: 'light',
      primary: {
        main: brandColors.primary,
        light: alpha(brandColors.primary, 0.05),
        dark: brandColors.secondary,
        contrastText: palette.white,
      },
      secondary: {
        main: palette.gray[100],
        light: palette.gray[50],
        dark: palette.gray[200],
        contrastText: palette.gray[800],
      },
      success: {
        main: '#2e7d32',
        light: '#4caf50',
        dark: '#1b5e20',
        contrastText: palette.white,
      },
      warning: {
        main: '#ed6c02',
        light: '#ff9800',
        dark: '#e65100',
        contrastText: palette.white,
      },
      error: {
        main: '#d32f2f',
        light: '#f44336',
        dark: '#c62828',
        contrastText: palette.white,
      },
      background: {
        default: backgrounds.light.default,
        paper: backgrounds.light.paper,
      },
      text: {
        primary: palette.gray[900],
        secondary: palette.gray[600],
      },
    },
    customBackgrounds: {
      stepCard: backgrounds.light.paper,
      footer: backgrounds.light.footer,
      modal: backgrounds.light.modal,
      card: backgrounds.light.card,
      landing: backgrounds.light.default,
    },
    commonBackgrounds: {
      cardBg: alpha(palette.gray[200], 0.3), // Light theme appropriate background
      glassBg: alpha(palette.gray[100], 0.8), // Light theme glass effect
    },
    textColors: {
      muted: alpha(palette.gray[600], 0.8), // Light theme muted text
    },
  };

  return createTheme({
    ...baseTheme,
    components: createComponentOverrides('light'),
    cssVariables: true, // Enable CSS variables for theming
  });
};

// Create the theme instances
export const darkTheme = createDarkTheme();
export const lightTheme = createLightTheme();
